/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import H1 from '@base/H1/H1';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Paper from '@base/Paper/Paper';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { getImage } from '@utils/imageHelper';
import useWindowSize from '@hooks/useWindowSize';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getCMSDataDescription, getisLoading } from '@selectors/careers';

const Wrapper = styled(Paper)`
  grid-column: span 24;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-height: 1338px;
  padding: 140px;
  padding-left: 116px;
  padding-right: 165px;
  ${mediaQuery.uptoTablet} {
    height: auto;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_12B};
    padding-top: ${Spacings.SPACING_15B};
    padding-bottom: ${Spacings.SPACING_24B};
  }
  ${mediaQuery.uptoMobile} {
    height: auto;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    padding-top: ${Spacings.SPACING_15B};
    padding-bottom: ${Spacings.SPACING_24B};
    justify-content: flex-start;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;
const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 429px;

  ${mediaQuery.uptoMobile} {
    width: auto;
    height: auto;
    margin-bottom: ${Spacings.SPACING_12B};
  }
`;
const HeadLine = styled(H1)`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_4B};
  font-size: 40px;
  line-height: 120%;

  ${mediaQuery.uptoMobile} {
    line-height: 150%;
    font-size: ${Fonts.H4};
    margin: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;
const SubHeadline = styled(P1)`
  font-size: ${Fonts.H4};
  margin-bottom: 70px;

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
    width: 278px;
    font-size: ${Fonts.SMALL};
  }
`;
const StyledAnchor = styled.a`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H4};
  color: var(--color-i-blue);
  text-align: left;
  margin-right: auto;
  border-bottom: 1px dashed var(--color-i-blue);

  ${mediaQuery.uptoMobile} {
    margin: auto;
    font-size: ${Fonts.H5};
  }
`;
const TestimonyCardsDiv = styled.div`
  display: flex;
  flex-direction: column;
`;
const TestimonyCardWrapper = styled.div`
  position: relative;
  background: ${({ $color }) => $color};
  width: 560px;
  height: 334px;
  margin-bottom: ${Spacings.SPACING_8B};
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  text-align: left;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    width: 373px;
    height: 222px;
  }
  ${mediaQuery.uptoMobile} {
    width: 328px;
    height: 220px;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;
const CardHeadLine = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_14B};
  margin-left: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_9B};
    margin-left: ${Spacings.SPACING_6B};
    font-size: ${Fonts.H5};
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_8B};
    margin-left: ${Spacings.SPACING_6B};
    font-size: ${Fonts.H5};
  }
`;
const StyledAvatar = styled.img`
  border-radius: 50%;
  object-fit: cover;
  user-select: none;
`;

const CardTextWrapper = styled.div`
  margin-left: 10px;
  align-self: center;
`;
const NameText = styled(H4)`
  line-height: 120%;
  font-weight: ${Fonts.SEMIBOLD};
  ${mediaQuery.uptoMobile} {
    line-height: 150%;
  }
`;
const PositionSpan = styled.span`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H5};
  color: var(--color-text-secondary);
  line-height: 150%;
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H7};
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H7};
  }
`;
const TestimonyText = styled(P1)`
  margin: 0 30px;
  margin-top: 30px;
  margin-bottom: ${Spacings.SPACING_12B};
  font-size: ${Fonts.H3};
  line-height: 125%;
  ${mediaQuery.uptoTablet} {
    line-height: 129%;
    margin-right: ${Spacings.SPACING_6B};
    margin-top: ${Spacings.SPACING_5B};
    margin-bottom: ${Spacings.SPACING_8B};
    font-size: ${Fonts.H6};
  }
  ${mediaQuery.uptoMobile} {
    margin: 0 21px;
    margin-top: ${Spacings.SPACING_5B};
    margin-bottom: ${Spacings.SPACING_8B};
    font-size: ${Fonts.H6};
  }
`;
const MaskImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const TestimonyCard = ({ data }) => {
  const { isMobile } = useWindowSize();
  return (
    <TestimonyCardWrapper $color={data.cardColor}>
      <CardHeadLine>
        <StyledAvatar
          src={getImage(data.img)}
          width={isMobile ? 48 : 64}
          height={isMobile ? 48 : 64}
        />
        <CardTextWrapper>
          <NameText>{data.name}</NameText>
          <PositionSpan>{data.position}</PositionSpan>
        </CardTextWrapper>
      </CardHeadLine>
      <TestimonyText>{data.testimony}</TestimonyText>
      <MaskImage>
        <img
          src={getImage(data.mask)}
          alt="mask"
          width={isMobile ? 328 : 560}
          height={isMobile ? 213 : 334}
        />
      </MaskImage>
    </TestimonyCardWrapper>
  );
};

const TeamTestimonials = () => {
  const [teamTestimonialData, setTeamTestimonialData] = useState([]);
  const [vlogLink, setVlogLink] = useState('');
  const { isMobile } = useWindowSize();
  const cmsData = useSelector(getCMSDataDescription);
  const isLoading = useSelector(getisLoading);

  useEffect(() => {
    if (cmsData.length > 0) {
      const CMSObject = JSON.parse(cmsData);
      setTeamTestimonialData(CMSObject.testimonials);
      setVlogLink(CMSObject.vlog);
    }
  }, [cmsData]);

  return (
    <Wrapper>
      <TextDiv>
        {isMobile || (
          <div>
            <img
              src={getImage('careers/Doublequotes.png')}
              alt="Doublequotes"
              width="64px"
              height="48px"
              style={{ margin: 'auto' }}
            />
          </div>
        )}
        <HeadLine>Hear from the team!</HeadLine>
        <SubHeadline>
          Get a sneak-peek into all the magic and madness that happens at
          Unacademy.
        </SubHeadline>
        {isLoading ? (
          <CircularLoader />
        ) : (
          <StyledAnchor
            target="_blank"
            rel="noopener noreferrer"
            href={vlogLink}
          >
            Watch Vlogs
          </StyledAnchor>
        )}
      </TextDiv>
      <TestimonyCardsDiv>
        {isLoading ? (
          <CircularLoader />
        ) : (
          teamTestimonialData.map((testimony) => (
            <TestimonyCard data={testimony} key={testimony.img} />
          ))
        )}
      </TestimonyCardsDiv>
    </Wrapper>
  );
};
export default TeamTestimonials;
